<template>
  <div
    v-if="workspaceId"
    ref="rootRef"
    class="relative"
  >
    <Button
      variant="outline"
      size="icon"
      title="Send feedback"
      @click="toggle"
    >
      <MessageSquareText class="h-[18px] w-[18px]" />
      <span class="sr-only">Send feedback</span>
    </Button>

    <div
      v-show="open"
      class="absolute right-0 top-full z-50 mt-2 w-[380px] rounded-lg border bg-popover p-4 text-popover-foreground shadow-xl"
    >
      <div class="flex items-center justify-between">
        <h4 class="text-sm font-semibold">
          Quick Feedback
        </h4>
        <button
          type="button"
          class="text-muted-foreground hover:text-foreground"
          @click="close"
        >
          <X class="size-4" />
          <span class="sr-only">Close</span>
        </button>
      </div>
      <p class="mt-1 text-xs text-muted-foreground">
        Flag a bug or idea without losing what you're working on. We'll pick it up from here —
        <span class="font-medium text-foreground">{{ currentPageLabel }}</span>.
      </p>

      <Textarea
        v-model="message"
        placeholder="What were you doing, what happened, and what did you expect?"
        class="mt-3 min-h-[100px] text-sm"
      />
      <p
        v-if="error"
        class="mt-1 text-xs text-destructive"
      >
        {{ error }}
      </p>

      <div class="mt-3">
        <p class="mb-1.5 text-xs font-medium text-muted-foreground">
          Priority
        </p>
        <div class="flex gap-1.5">
          <button
            v-for="option in labelOptions"
            :key="option.value"
            type="button"
            class="flex-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors"
            :class="label === option.value ? priorityActiveClass[option.value] : 'border-input text-muted-foreground hover:bg-accent'"
            @click="label = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="mt-3 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          @click="fileUploaderRef?.triggerFileInput?.()"
        >
          <Paperclip class="size-3.5" />
          Attach file
        </Button>
      </div>

      <FileUploader
        ref="fileUploaderRef"
        v-model="attachments"
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.xls,.xlsx,.ppt,.pptx,.txt"
        multiple
        :max-files="5"
        :max-file-size="10"
      >
        <template #default="{ files, getFilePreview, deleteFile, formatFileSize }">
          <ul
            v-if="files.length"
            class="mt-3 space-y-1.5"
          >
            <li
              v-for="(file, index) in files"
              :key="index"
              class="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs"
            >
              <img
                v-if="file.type?.startsWith('image/')"
                :src="getFilePreview(file)"
                class="size-8 shrink-0 rounded object-cover"
                alt=""
              >
              <Paperclip
                v-else
                class="size-4 shrink-0 text-muted-foreground"
              />
              <span class="flex-1 truncate">{{ file.name }}</span>
              <span class="shrink-0 text-muted-foreground">{{ formatFileSize(file.size) }}</span>
              <button
                type="button"
                class="shrink-0 text-muted-foreground hover:text-destructive"
                @click="deleteFile(index)"
              >
                <X class="size-3.5" />
                <span class="sr-only">Remove</span>
              </button>
            </li>
          </ul>
        </template>
      </FileUploader>

      <div class="mt-4 flex items-center justify-between border-t pt-3">
        <RouterLink
          :to="feedbackListRoute"
          class="text-xs text-muted-foreground hover:text-primary hover:underline"
          @click="close"
        >
          View all feedback
        </RouterLink>
        <div class="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            @click="close"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            :loading="saving"
            @click="submit"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { MessageSquareText, Paperclip, X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import FileUploader from '@/components/common/uploader/FileUploader.vue'
import { useFeedbacksStore, useWorkspaceStore } from '@/stores'
import { feedbackLabels } from '@/lib/constants'

const route = useRoute()
const workspaceStore = useWorkspaceStore()
const feedbacksStore = useFeedbacksStore()

const rootRef = ref(null)
const fileUploaderRef = ref(null)

const open = ref(false)
const message = ref('')
const label = ref('medium')
const attachments = ref(null)
const error = ref('')
const saving = ref(false)

const labelOptions = feedbackLabels
const priorityActiveClass = {
  low: 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  medium: 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  high: 'border-red-500 bg-red-500/10 text-red-600 dark:text-red-400',
}

const workspaceId = computed(() => route.params.workspace_id || workspaceStore.getWorkspaceID)
const feedbackListRoute = computed(() => ({
  name: 'workspace-feedbacks',
  params: { workspace_id: workspaceId.value },
}))

const currentPageLabel = computed(() => route.meta?.pageTitle || route.meta?.title || 'this page')

const attachmentFiles = computed(() =>
  Array.isArray(attachments.value) ? attachments.value.filter((file) => file instanceof File) : [],
)

watch(message, () => {
  if (error.value) error.value = ''
})

function toggle() {
  open.value = !open.value
}

function close() {
  open.value = false
}

function resetForm() {
  message.value = ''
  label.value = 'medium'
  attachments.value = null
  error.value = ''
  fileUploaderRef.value?.reset?.()
}

onClickOutside(rootRef, () => {
  open.value = false
})

onKeyStroke('Escape', () => {
  if (open.value) open.value = false
})

async function submit() {
  const trimmedMessage = message.value.trim()
  if (!trimmedMessage) {
    error.value = 'Let us know what happened.'
    return
  }

  saving.value = true
  try {
    const subject = `Quick feedback — ${currentPageLabel.value}`
    const fullMessage = `${trimmedMessage}\n\n— Reported from: ${currentPageLabel.value} (${window.location.pathname})`
    const files = attachmentFiles.value

    const payload = files.length ? new FormData() : { subject, message: fullMessage, label: label.value }
    if (payload instanceof FormData) {
      payload.append('subject', subject)
      payload.append('message', fullMessage)
      payload.append('label', label.value)
      files.forEach((file) => payload.append('attachments[]', file))
    }

    await feedbacksStore.CreateFeedback(payload)
    resetForm()
    close()
  } finally {
    saving.value = false
  }
}
</script>
